import { useEffect, useState } from 'react';

import { apiClient } from '@/api/client';

import { isAnonymousRuntime } from './anonymousRuntime';
import type { Role } from './useRoles';

export type MeResponse = {
  oid: string;
  name: string | null;
  email: string | null;
  preferred_username: string | null;
  roles: Role[];
  /**
   * Advisor profile, sourced from GAMX advisor-info at ISAM login (see
   * GamxAdvisorDetailClient) and surfaced as claims on the BFF session.
   * Absent for Entra (originator / approver) sessions and for advisors whose
   * profile lookup failed — the runtime prefill treats missing as "no value"
   * rather than blanking a field the advisor may have typed into.
   */
  phoneNumber?: string | null;
  dealerCode?: string | null;
  representativeCodes?: string[] | null;
  /** The user's names unjoined — BMO's signer records take them separately (spec §5.4).
   *  Advisors get them from GAMX advisor-info; Entra users get them by splitting `name`
   *  at the first space, so a middle name lands on `lastName`. */
  firstName?: string | null;
  lastName?: string | null;
  /** Unix seconds — when the BFF's stored IdP access token expires. Informational. */
  accessTokenExpiresAt: number;
  /** Unix seconds — the absolute session cap (fixed from login; activity can't extend it). */
  sessionExpiresAt: number;
  /** Server-driven cadence, in seconds, for the session timer's refresh interval. */
  refreshIntervalSeconds: number;
  /**
   * Unix seconds — when the ISAM refresh token behind the next grant expires. This is the
   * deadline that ENDS the session; `accessTokenExpiresAt` above is only the thing a refresh
   * renews.
   *
   * Null for Entra sessions, which hold no refresh token, and absent from a backend predating
   * the field. Both mean "unknown", never "expired" — see sessionRefreshLock.ts.
   */
  refreshTokenExpiresAt?: number | null;
};

// Module-level request deduplication: one in-flight fetch shared across all callers.
// Cleared on logout via invalidateSession().
let _promise: Promise<MeResponse | null> | null = null;
let _cached: MeResponse | null | undefined = undefined;
/**
 * When the last /me attempt FAILED, not when it resolved to "no session".
 *
 * A failure used to be cached like an answer (`_cached = null`), which the
 * early return then treated as final for the lifetime of the page. One blip —
 * an API redeploy, a dropped connection, a cold Lambda — permanently and
 * SILENTLY disabled everything that reads the session: advisor-profile
 * prefill, role checks, the session timer. Nothing surfaced an error; fields
 * simply stopped populating, which reads as "the feature is broken".
 *
 * So a failure is now retryable. The cooldown keeps a genuinely dead endpoint
 * from turning every mount into a request — FieldRenderer calls useSession per
 * field, so an unbounded retry would be a storm.
 */
let _failedAt: number | null = null;
const FAILURE_RETRY_MS = 5_000;

/** Mounted {@link useSession} consumers, notified when the cached session is patched. */
const _subscribers = new Set<(user: MeResponse | null) => void>();

function fetchSessionOnce(): Promise<MeResponse | null> {
  // The public customer runtime never has a session, so asking is pure cost — and it is not
  // a one-off cost: FieldRenderer calls useSession once per field, StepContent remounts the
  // whole field tree on every step change, and the retry policy below re-asks after each
  // cooldown. That turned a page change into a fresh /me, which breaks the one rule that
  // runtime has (PDF generation is its only request). See auth/anonymousRuntime.ts.
  if (isAnonymousRuntime()) return Promise.resolve(null);
  // A real answer (including a legitimate "no session" null from a 401 that
  // resolved) is cached; only transport failures are retried.
  if (_cached !== undefined && _cached !== null) return Promise.resolve(_cached);
  if (_cached === null && _failedAt !== null && Date.now() - _failedAt < FAILURE_RETRY_MS) {
    return Promise.resolve(null);
  }
  if (_promise) return _promise;
  _promise = apiClient
    .get<MeResponse>('/me')
    .then((res) => {
      _cached = res.data;
      _failedAt = null;
      _promise = null;
      return _cached;
    })
    .catch(() => {
      _cached = null;
      _failedAt = Date.now();
      _promise = null;
      return null;
    });
  return _promise;
}

/** Test seam — the retry policy above is the whole point of the module, and
 *  it isn't reachable through the hook without a DOM. Not for app code. */
export const useSessionInternals = { fetchSessionOnce, FAILURE_RETRY_MS, endSession };

/** Call after logout to force a fresh /me fetch on the next navigation. */
export function invalidateSession(): void {
  _cached = undefined;
  _promise = null;
  _failedAt = null;
}

/**
 * Publishes "there is no session" to every MOUNTED consumer, immediately.
 *
 * Distinct from {@link invalidateSession}, which only drops the cache so the NEXT mount
 * re-asks: components already on screen keep their own `user` state and never hear about it.
 * That gap is visible — the header goes on naming a user whose session the server has
 * already discarded, because nothing re-rendered it.
 *
 * Used by the 401 guard below, which is the one moment we are told authoritatively.
 */
function endSession(): void {
  if (_cached === null) return;
  _cached = null;
  _promise = null;
  for (const notify of _subscribers) notify(null);
}

/**
 * A 401 from any authenticated call is the server saying this browser has no session — so
 * the cached /me answer is now false, and anything drawn from it (the header's name and
 * role, most visibly) has to stop claiming otherwise.
 *
 * Registered here rather than in `api/client.ts` on purpose: the cache this invalidates is
 * this module's, and having the client import it back would make `client → useSession →
 * client` a cycle.
 *
 * The error is re-thrown untouched. Existing 401 handling — useSessionTimer's redirect on a
 * failed refresh, each call site's own error branch — must still see exactly what it saw
 * before; this only makes sure the identity on screen does not outlive the session.
 *
 * `publicApiClient` is deliberately NOT guarded: the customer runtime has no session to end,
 * and a 401 there says nothing about one.
 */
apiClient.interceptors.response.use(undefined, (error: unknown) => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 401) endSession();
  return Promise.reject(error);
});

/**
 * Replaces the cached session's representative codes with a freshly-read set, and re-renders
 * every mounted consumer.
 *
 * The codes on `/me` are a snapshot taken at ISAM login and replayed as claims for the life of
 * the session; `POST /auth/advisor/rep-codes/refresh` re-reads them from GAMX, and this is how
 * that answer reaches the form. Nothing else on the session is touched — the refresh is
 * deliberately codes-only.
 *
 * A NEW object is published rather than a mutation: FormShell's advisor-prefill effect depends
 * on the `user` identity, and that effect re-running is precisely what clears a pick the advisor
 * no longer holds and auto-selects a list that has collapsed to one code. An unchanged answer
 * publishes nothing, so a step the advisor walks back onto doesn't churn every mounted field.
 *
 * No-ops when there is no cached session at all: there is nothing to patch, and inventing a
 * session object out of a codes response would fabricate an identity /me never gave us.
 */
export function applySessionRepCodes(codes: string[]): void {
  if (_cached === undefined || _cached === null) return;
  const current = _cached.representativeCodes ?? [];
  if (current.length === codes.length && current.every((code, i) => code === codes[i])) return;
  _cached = { ..._cached, representativeCodes: codes };
  for (const notify of _subscribers) notify(_cached);
}

/**
 * Resolves the current server-side session by calling GET /api/v1/me.
 * Multiple simultaneous callers share one in-flight request; the result is cached
 * for the lifetime of the page (invalidated on logout).
 */
export function useSession(): { user: MeResponse | null; loading: boolean } {
  const [user, setUser] = useState<MeResponse | null>(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined);

  useEffect(() => {
    // Every live consumer is subscribed, not just the one that triggered a
    // patch: the cache is module-level but the state above is per-hook, so a
    // write to `_cached` on its own re-renders nobody. FieldRenderer calls this
    // hook once per FIELD, and they all have to agree about the session.
    _subscribers.add(setUser);

    // Only short-circuit on a REAL cached session. A cached null may be a
    // past failure, and fetchSessionOnce decides whether that is retryable —
    // short-circuiting here would reinstate the poisoned-cache bug one layer
    // up, since a mount after the cooldown would never re-ask.
    if (_cached !== undefined && _cached !== null) {
      setUser(_cached);
      setLoading(false);
      return () => {
        _subscribers.delete(setUser);
      };
    }
    let alive = true;
    void fetchSessionOnce().then((data) => {
      if (!alive) return;
      setUser(data);
      setLoading(false);
    });
    return () => {
      alive = false;
      _subscribers.delete(setUser);
    };
  }, []);

  return { user, loading };
}
